/*
 * Copyright (c) 2023-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, ElementRef, OnInit, signal, ViewChild} from '@angular/core';
import {HttpEventType, HttpResponse} from '@angular/common/http';
import JSZip from 'jszip';
import {GMNetlistCompareResultsHolder} from '../../model/gmNetlistCompareResultsHolder.model';
import {EsciComponent} from '../esci.component';
import {CommonHelperService} from '../../service/common-helper.service';

@Component({
    selector: 'app-netlist-compare',
    templateUrl: './netlist-compare.component.html',
    styleUrls: ['./netlist-compare.component.scss'],
    standalone: false
})
export class NetlistCompareComponent extends EsciComponent implements OnInit {
  // Input fields in html for clear buttons
  @ViewChild('hrnFile1Tag') hrnFile1Ref: ElementRef;
  @ViewChild('hrnFile2Tag') hrnFile2Ref: ElementRef;
  @ViewChild('prolFileTag') prolFileRef: ElementRef;
  @ViewChild('plocFileTag') plocFileRef: ElementRef;
  hrnFile1: Blob | null;
  hrnFile2: Blob | null;
  prolFile: File | null;
  plocFile: File | null;
  progress = signal(0);
  userPrompt: string | null;
  showFileInput = true;
  singleMismatch = false;
  permissionUrl = '/esciUI/netlistComparePermission';
  harnessName = '';
  gmNetlistCompareResultsHolder = new GMNetlistCompareResultsHolder;

  private setProgress(value: number): void {
    this.progress.set(value);
  }

  ngOnInit(): void {
    this.permission.set(false);
    this.esciService.getPermissionAndFileSizeLimit(this);
  }

  clearHrn1File() {
    this.hrnFile1Ref.nativeElement.value = null;
    this.hrnFile1 = null;
  }
  clearHrn2File() {
    this.hrnFile2Ref.nativeElement.value = null;
    this.hrnFile2 = null;
  }
  clearProlFile() {
    this.prolFileRef.nativeElement.value = null;
    this.prolFile = null;
  }
  clearPlocFile() {
    this.plocFileRef.nativeElement.value = null;
    this.plocFile = null;
  }

  selectHrn1Files(event: any): void {
    this.selectHrnFile(event, (content) => {
      this.hrnFile1 = content;
    });
  }

  selectHrn2Files(event: any): void {
    this.selectHrnFile(event, (content) => {
      this.hrnFile2 = content;
    });
  }

  selectHrnFile <TResult1 = Blob> (event: any, onfulfilled?: ((value: Blob) => TResult1 | PromiseLike<TResult1>) | undefined | null) {
    if (this.permission()) {
      if (event.target.files) {
        const zip = new JSZip();
        for (let i = 0; i < event.target.files.length; i++) {
          const file = event.target.files.item(i);
          if (file.name.endsWith('.impf.hrn') ||
            file.name.endsWith('.impf.cmp')) {
            zip.file(file.name, file);
          }
        }
        zip.generateAsync({type: 'blob'}).then(onfulfilled);
      }
    }
  }

  selectProlFile(event: any): void {
    if (event.target.files && this.permission()) {
      this.prolFile = event.target.files.item(0);
    }
  }

  selectPlocFile(event: any): void {
    if (event.target.files && this.permission()) {
      this.plocFile = event.target.files.item(0);
    }
  }
  handleResultCodes() {
    const code = this.gmNetlistCompareResultsHolder?.resultCodeSet[0];
    this.userPrompt = null;
    this.harnessName = '';
    if (code === 'NO_HRN_CMP_PAIR_SET1') {
      this.showFileInput = true;
      this.messageService.processError('No valid pair of hrn/cmp files found for input 1. Please check the files selected.');
    } else if (code === 'NO_HRN_CMP_PAIR_SET2') {
      this.showFileInput = true;
      this.messageService.processError('No valid pair of hrn/cmp files found for input 2. Please check the files selected.');
    } else if (code === 'NO_HARNESS_OVERLAP') {
      this.showFileInput = true;
      this.userPrompt = 'No matching harnesses found. Please check the files selected.' +
      'Harnesses from input 1: ' + this.formatHarnessSet(this.gmNetlistCompareResultsHolder?.harness1Set) +
        '. Harnesses from input 2: ' + this.formatHarnessSet(this.gmNetlistCompareResultsHolder?.harness2Set) + '.';
    } else if (code === 'SINGLE_MISMATCH_HARNESS_OVERLAP') {
      this.showFileInput = false;
      this.singleMismatch = true;
      this.userPrompt = 'No matching harnesses found, would you like to compare '
        + this.gmNetlistCompareResultsHolder?.harness1Set[0] + ' to '
        + this.gmNetlistCompareResultsHolder?.harness2Set[0] + '?';
    } else if (code === 'MULTIPLE_MATCHING_HARNESSES') {
      this.showFileInput = false;
      this.userPrompt = 'Multiple matching harnesses found, please select the harness to compare.';
    } else if (code === 'TARGET_HARNESS_NOT_FOUND') {
      this.showFileInput = true;
      this.messageService.processError('Selected harness not found in the files provided. Please try again.');
    } else if (code === 'NO_FILES_FOUND') {
      this.showFileInput = true;
      this.messageService.processError('No valid files provided. Please try again.');
    } else if (code === 'SUCCESS') {
      this.showFileInput = true;
      const blobFile = new Blob([this.s2ab(window.atob(this.gmNetlistCompareResultsHolder.compareResultsWB_File))], {
        type: ''
      });
      const fileName = 'NetlistCompare' + CommonHelperService.getDateString()  + '.xlsx';
      CommonHelperService.downloadFile(new Blob([blobFile]), fileName);
      this.messageService.processSuccess('Netlist Compare Report successfully generated. Please see the downloaded file.');
    } else {
      this.showFileInput = true;
    }
  }

  generateReport(): void {
    if (this.hrnFile1 && this.hrnFile2 && this.areFileSizesValid()) {
      // this.showHarnessBuckets = false;
      this.setProgress(10);
      this.userPrompt = null;
      if (this.singleMismatch) {
        // user ran files previously that resulted in single mismatch
        // only if they hit generate report will we set harness name here
        this.harnessName = this.gmNetlistCompareResultsHolder?.harness1Set[0] + '_'
          + this.gmNetlistCompareResultsHolder?.harness2Set[0];
      }
      this.singleMismatch = false;

      this.uploadService.uploadFilesForNetlistCompare(this.hrnFile1, this.hrnFile2, this.prolFile,
          this.plocFile, this.harnessName).subscribe({
        next: (event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            this.setProgress(Math.round(100 * event.loaded / event.total));
          } else if (event instanceof HttpResponse) {
            this.setProgress(0);
            if (event.body) {
              this.gmNetlistCompareResultsHolder = event.body;
              this.handleResultCodes();
            } else {
              this.messageService.processError('Could not run the Netlist Compare. Please check the file provided and try again later.');
            }
          }
        },
        error: (err: any) => {
          this.setProgress(0);
          console.log(err);
          this.messageService.processError('Could not run the Netlist Compare. Please check the file provided and try again later.');
        }
      });
    }
  }

  // this method helps with the base 64 decoding for our excel files to become blobs
  s2ab(s: string) {
    const buf = new ArrayBuffer(s.length);
    const view = new Uint8Array(buf);
    for (let i = 0; i != s.length; ++i) { view[i] = s.charCodeAt(i) & 0xFF; }
    return buf;
  }

  private areFileSizesValid(): boolean {
    let valid = true;
    if (this.hrnFile1 && this.hrnFile1.size > this.MAX_FILE_SIZE) {
      this.messageService.processError('Input file is too large. Please select a smaller file.');
      valid = false;
    }
    return valid;
  }

  private formatHarnessSet(harnessSet: string[]) {
    if (harnessSet) {
      let returnString = harnessSet.join(', ');
      if (returnString.length > 20) {
        returnString = returnString.substring(0, 20) + '...';
      }
      return '[' + returnString + ']';
    }
    return '';
  }
}
